import { useEffect, useState } from 'react';
import {
    RichEditor,
    isDocEmpty,
    collectDocImageFileIds,
    applyResolvedUrlsToDoc
} from '@odo/shared-ui';
import type { RichEditorImageUploadHandler, TiptapDoc } from '@odo/shared-ui';
import { resolveInternalImageUrls } from '../../../services/internalImages';
import type { PatientNote } from '../../../services/patientNotes';

// Read-only render only — RichEditor never calls these while readOnly.
const NOOP_CHANGE = (): void => {};
const NOOP_UPLOAD: RichEditorImageUploadHandler = () => Promise.reject(new Error('read-only'));

interface NoteBodyProps {
    note: PatientNote;
    compact?: boolean;
}

/**
 * Renders a note's content. Rich notes (with `bodyDoc`) render read-only through
 * the shared RichEditor after their image references are re-resolved to fresh
 * signed URLs. Legacy notes (no `bodyDoc`) fall back to the plain-text `body`.
 */
function NoteBody({ note, compact = false }: NoteBodyProps) {
    const hasDoc = !!note.bodyDoc && !isDocEmpty(note.bodyDoc);
    const [doc, setDoc] = useState<TiptapDoc | null>(note.bodyDoc ?? null);

    useEffect(() => {
        if (!note.bodyDoc) {
            setDoc(null);
            return;
        }
        setDoc(note.bodyDoc);
        const fileIds = collectDocImageFileIds(note.bodyDoc);
        if (fileIds.length === 0) {
            return;
        }
        let cancelled = false;
        resolveInternalImageUrls(note.patientId, fileIds)
            .then((urls) => {
                if (!cancelled) {
                    setDoc(applyResolvedUrlsToDoc(note.bodyDoc!, urls));
                }
            })
            .catch(() => {
                /* leave unresolved — text stays intact, images may break */
            });
        return () => {
            cancelled = true;
        };
    }, [note.bodyDoc, note.patientId]);

    if (!hasDoc || !doc) {
        return (
            <p
                className={`${compact ? 'text-[11px]' : 'text-[12px]'} text-gray-700 font-inter leading-relaxed whitespace-pre-wrap`}
            >
                {note.body}
            </p>
        );
    }

    return (
        <div className={`note-rich-body font-inter${compact ? ' note-rich-body--compact' : ''}`}>
            <RichEditor
                value={doc}
                onChange={NOOP_CHANGE}
                onImageUpload={NOOP_UPLOAD}
                readOnly
                chrome="bare"
            />
        </div>
    );
}

export { NoteBody };
